import { UNSAFE_createBrowserHistory } from 'react-router-dom';

import { ClientRouterPopStateDecision, type ClientRouterPopState } from './types';

type BeforePopState = (state: ClientRouterPopState) => ClientRouterPopStateDecision;

const BROWSER_HISTORY_GUARD_KEY = '__glaiBrowserHistoryGuard';

interface BrowserHistoryGuardState {
	beforePopState: BeforePopState;
	installed: boolean;
	skipRouterNotification: boolean;
}

const guardHost = globalThis as typeof globalThis & {
	[BROWSER_HISTORY_GUARD_KEY]?: BrowserHistoryGuardState;
};
const guardState = guardHost[BROWSER_HISTORY_GUARD_KEY] ?? {
	beforePopState: () => ClientRouterPopStateDecision.Continue,
	installed: false,
	skipRouterNotification: false
};
guardHost[BROWSER_HISTORY_GUARD_KEY] = guardState;

/**
 * This capture listener is installed before React Router subscribes to the
 * application-owned history. A blocked traversal therefore reaches neither
 * React Router nor route-local popstate listeners until confirmation replays it.
 * A traversal handled by the page remains visible to its native listener while
 * the history adapter below withholds it from React Router.
 */
if (typeof window !== 'undefined' && !guardState.installed) {
	window.addEventListener(
		'popstate',
		(event) => {
			const as = `${window.location.pathname}${window.location.search}${window.location.hash}`;
			const decision = guardState.beforePopState({ as, options: {}, state: event.state, url: as });
			guardState.skipRouterNotification = decision === ClientRouterPopStateDecision.HandleLocally;
			if (decision === ClientRouterPopStateDecision.Block) event.stopImmediatePropagation();
		},
		true
	);
	guardState.installed = true;
}

const browserHistory = UNSAFE_createBrowserHistory({ v5Compat: true });
const listenToBrowserHistory = browserHistory.listen.bind(browserHistory);

// React Router owns the history listener, while feature pages own ordinary
// popstate listeners. Suppress only the former when a page has declared that it
// will synchronize the traversal itself.
browserHistory.listen = (listener) =>
	listenToBrowserHistory((update) => {
		if (guardState.skipRouterNotification) {
			guardState.skipRouterNotification = false;
			return;
		}
		listener(update);
	});

export const applicationBrowserHistory = browserHistory;

export function setBrowserBeforePopState(callback: BeforePopState): () => void {
	guardState.beforePopState = callback;
	return () => {
		if (guardState.beforePopState === callback) {
			guardState.beforePopState = () => ClientRouterPopStateDecision.Continue;
		}
	};
}
